import React, { useState } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, TextField, InputAdornment, Button,
  Grid, FormControl, InputLabel, Select, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, Tooltip, IconButton, Skeleton,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import ManageSearchIcon  from "@mui/icons-material/ManageSearch";
import TuneIcon          from "@mui/icons-material/Tune";
import OpenInNewIcon     from "@mui/icons-material/OpenInNew";
import BoltIcon          from "@mui/icons-material/Bolt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { isAutheticated } from "../auth/helper";
import { searchArticles } from "./helper/adminapicall";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) + " " +
    dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const FeedSearch = () => {
  const { admin, token } = isAutheticated();

  const [query,    setQuery]    = useState("");
  const [symbol,   setSymbol]   = useState("");
  const [source,   setSource]   = useState("");
  const [breaking, setBreaking] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");

  const [results,  setResults]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = (p = 0) => {
    if (!query.trim() && !symbol && !source && !breaking && !dateFrom && !dateTo) return;
    setLoading(true);
    setPage(p);
    const qs = new URLSearchParams();
    if (query.trim()) qs.set("q",           query.trim());
    if (symbol)       qs.set("symbols",     symbol.toUpperCase());
    if (source)       qs.set("source_code", source.toUpperCase().replace(/\s+/g, "_"));
    if (breaking)     qs.set("is_breaking", breaking);
    if (dateFrom)     qs.set("from",        dateFrom);
    if (dateTo)       qs.set("to",          dateTo);
    qs.set("page",  String(p + 1));
    qs.set("limit", "25");
    searchArticles(admin._id, token, "?" + qs.toString())
      .then(d => {
        setResults(Array.isArray(d?.articles) ? d.articles : []);
        setTotal(d?.total ?? 0);
        setSearched(true);
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => { e.preventDefault(); doSearch(0); };

  const handleReset = () => {
    setQuery(""); setSymbol(""); setSource(""); setBreaking("");
    setDateFrom(""); setDateTo(""); setResults([]); setTotal(0);
    setSearched(false); setPage(0);
  };

  return (
    <Base>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Feed Search</Typography>
        <Typography variant="body2" color="text.secondary">
          Full-text search across all ingested articles
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <TuneIcon sx={{ color: "text.secondary", fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600}>Search Filters</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Keyword" placeholder="Search headline and article text…"
                value={query} onChange={e => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Symbol" placeholder="e.g. RELIANCE"
                value={symbol} onChange={e => setSymbol(e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Source code" placeholder="e.g. ABP_NEWS"
                value={source} onChange={e => setSource(e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Breaking</InputLabel>
                <Select value={breaking} label="Breaking" onChange={e => setBreaking(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Breaking only</MenuItem>
                  <MenuItem value="false">Non-breaking</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Published from" type="date"
                InputLabelProps={{ shrink: true }}
                value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Published to" type="date"
                InputLabelProps={{ shrink: true }}
                value={dateTo} onChange={e => setDateTo(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }} />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" size="small" onClick={handleReset}>Reset</Button>
                <Button type="submit" variant="contained" size="small" startIcon={<ManageSearchIcon />} disabled={loading}>
                  Search
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="body2" color="text.secondary">
            {!searched
              ? "Enter a search query above"
              : loading
                ? "Searching…"
                : `${total.toLocaleString()} result${total !== 1 ? "s" : ""}`
            }
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Headline</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Symbols</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Published</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                    </TableRow>
                  ))
                : results.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <ManageSearchIcon sx={{ fontSize: 40, color: "text.disabled", opacity: 0.4 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {searched ? "No results found" : "Search results will appear here"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : results.map(a => (
                    <TableRow key={a._id} hover>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {a.is_breaking && (
                            <Tooltip title="Breaking">
                              <BoltIcon sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }} />
                            </Tooltip>
                          )}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 380 }}>
                            {a.clean_headline || a.headline}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {a.source_name || a.source_code || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(a.symbols || []).slice(0, 4).map(s => (
                            <Chip key={s} label={s} size="small" sx={{ fontSize: "0.65rem", height: 18 }} />
                          ))}
                          {(a.symbols?.length || 0) > 4 && (
                            <Chip label={`+${a.symbols.length - 4}`} size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 18 }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {fmtDate(a.published_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {a.url && (
                          <Tooltip title="Open article">
                            <IconButton size="small" href={a.url} target="_blank" rel="noopener noreferrer">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>

        {searched && total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            onPageChange={(_, p) => doSearch(p)}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        )}
      </Paper>
    </Base>
  );
};

export default FeedSearch;
